// src/context/WalletContext.js

import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { NETWORK_CONFIG } from '../contracts/constants';
import { toast } from 'react-toastify';

const ALCHEMY_URL = process.env.REACT_APP_ALCHEMY_RPC_URL;

export const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null); // MetaMask or fallback
  const [chainId, setChainId] = useState(null);
  const [isChangingWallet, setIsChangingWallet] = useState(false);

  // A fallback read-only provider if needed
  const fallbackProviderRef = useRef(null);
  if (!fallbackProviderRef.current) {
    fallbackProviderRef.current = new ethers.providers.JsonRpcProvider(
      ALCHEMY_URL || NETWORK_CONFIG.rpcUrl
    );
  }

  // For reconnect logic
  const reconnectAttempted = useRef(false);

  /**
   * Resets wallet state to fallback
   */
  const resetWalletState = useCallback(() => {
    setAccount('');
    setProvider(fallbackProviderRef.current);
    setChainId(null);
    localStorage.removeItem('walletConnected');
    localStorage.removeItem('connectionType');
  }, []);

  /**
   * Disconnect manually
   */
  const disconnectWallet = useCallback(() => {
    resetWalletState();
  }, [resetWalletState]);

  /**
   * Switch to Unichain (chainId=130, hex=0x82)
   */
  const switchToUnichainNetwork = useCallback(async () => {
    if (!window.ethereum) {
      throw new Error('No injected wallet found (MetaMask?).');
    }
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: NETWORK_CONFIG.chainIdHex }], // "0x82"
      });
      return true;
    } catch (switchError) {
      // If chain not added in MM, add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: NETWORK_CONFIG.chainIdHex,
                chainName: NETWORK_CONFIG.name,
                nativeCurrency: NETWORK_CONFIG.nativeCurrency,
                rpcUrls: [NETWORK_CONFIG.rpcUrl],
                blockExplorerUrls: [NETWORK_CONFIG.blockExplorer],
              },
            ],
          });
          // Then switch
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: NETWORK_CONFIG.chainIdHex }],
          });
          return true;
        } catch (addError) {
          console.error(`Failed to add ${NETWORK_CONFIG.name}:`, addError);
          throw new Error(`Failed to add ${NETWORK_CONFIG.name} network. Please add it manually.`);
        }
      } else {
        console.error(`Failed to switch to ${NETWORK_CONFIG.name}:`, switchError);
        throw new Error(`Please switch to ${NETWORK_CONFIG.name} in MetaMask.`);
      }
    }
  }, []);

  /**
   * Connect to MetaMask
   */
  const connectMetaMask = useCallback(
    async (userInitiated = false) => {
      if (!window.ethereum) {
        if (userInitiated) toast.error("MetaMask is not installed");
        return false;
      }

      try {
        // Request account access
        const accounts = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });
        if (!accounts.length) {
          throw new Error("No accounts returned from MetaMask");
        }

        // Ensure correct chain
        await switchToUnichainNetwork();

        // Create Ethers provider with "any" so it doesn't lock to the first chain
        const web3Provider = new ethers.providers.Web3Provider(
          window.ethereum,
          "any"
        );

        // Check final chain
        const network = await web3Provider.getNetwork();
        setChainId(network.chainId);

        if (network.chainId !== NETWORK_CONFIG.chainId) {
          throw new Error(`Please connect to ${NETWORK_CONFIG.name} (chainId ${NETWORK_CONFIG.chainId}).`);
        }

        setAccount(accounts[0]);
        setProvider(web3Provider);

        localStorage.setItem('walletConnected', 'true');
        localStorage.setItem('connectionType', 'metamask');

        return true;
      } catch (error) {
        console.error('connectMetaMask error:', error);
        if (userInitiated) {
          toast.error(error.message || "Failed to connect wallet");
        }
        return false;
      }
    },
    [switchToUnichainNetwork]
  );

  /**
   * Let user pick a different account
   */
  const changeAccount = useCallback(async () => {
    setIsChangingWallet(true);
    if (!window.ethereum) {
      toast.error("MetaMask is not installed");
      setIsChangingWallet(false);
      return false;
    }

    try {
      // This triggers the account selection in MetaMask
      await window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }],
      });

      // Then get current accounts
      const accounts = await window.ethereum.request({
        method: 'eth_accounts',
      });
      if (!accounts.length) {
        throw new Error("No accounts returned after requesting permissions");
      }

      setAccount(accounts[0]);

      // Re-create the provider with "any"
      const web3Provider = new ethers.providers.Web3Provider(
        window.ethereum,
        "any"
      );
      setProvider(web3Provider);

      // Check chain
      const network = await web3Provider.getNetwork();
      setChainId(network.chainId);
      if (network.chainId !== NETWORK_CONFIG.chainId) {
        await switchToUnichainNetwork();
      }
      return true;
    } catch (error) {
      console.error("Error changing account:", error);
      return false;
    } finally {
      setIsChangingWallet(false);
    }
  }, [switchToUnichainNetwork]);

  /**
   * Try auto-reconnect on page load if user was connected
   */
  useEffect(() => {
    const attemptReconnect = async () => {
      if (reconnectAttempted.current) return;
      reconnectAttempted.current = true;

      const wasConnected = localStorage.getItem('walletConnected') === 'true';
      const savedConnectionType = localStorage.getItem('connectionType');

      if (wasConnected && savedConnectionType === 'metamask') {
        await connectMetaMask(false);
      }
    };
    attemptReconnect();
  }, [connectMetaMask]);

  /**
   * Listen for chain/account changes
   */
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      if (!accounts.length) {
        resetWalletState();
      } else if (accounts[0] !== account) {
        setAccount(accounts[0]);
        if (window.ethereum) {
          const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
          web3Provider.getNetwork().then((net) => {
            setChainId(net.chainId);
          });
          setProvider(web3Provider);
        }
      }
    };

    const handleChainChanged = async (chainIdHex) => {
      const newChainId = parseInt(chainIdHex, 16);
      setChainId(newChainId);
      if (account) {
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
        setProvider(web3Provider);
        const network = await web3Provider.getNetwork();
        if (network.chainId !== NETWORK_CONFIG.chainId) {
          try {
            await switchToUnichainNetwork();
          } catch (err) {
            console.error("Failed to re-switch to Unichain:", err);
          }
        }
      }
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    const handleDisconnect = () => {
      if (account) {
        resetWalletState();
      }
    };
    // Some wallets emit "disconnect"
    window.ethereum.on('disconnect', handleDisconnect);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('disconnect', handleDisconnect);
    };
  }, [account, resetWalletState, switchToUnichainNetwork]);

  const contextValue = {
    account,
    provider,
    chainId,
    isChangingWallet,
    connectMetaMask,
    disconnectWallet,
    changeAccount,
    switchToUnichainNetwork,
  };

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

export default WalletProvider;